import { useState, useRef, useEffect, FormEvent } from 'react';
import { PHONE_1, PHONE_1_DISPLAY, PHONE_2, PHONE_2_DISPLAY } from '../../../components/feature/TopBar';

const FORM_URL = 'https://readdy.ai/api/form/d72p8gpsakbatd8b3ok0';

const serviceOptions = [
  'Koltuk Yıkama',
  'Sandalye Yıkama',
  'Yatak Yıkama',
  'Petek Temizliği',
];

const advantages = [
  '18 yıl tecrübeli uzman ekip',
  'Alman Karcher makinesi',
  'Evde hizmet, taşıma yok',
  'Anadolu & Avrupa Yakası geneli',
  'Aynı gün randevu imkânı',
];

const WA_LINK = `https://wa.me/90${PHONE_1}?text=${encodeURIComponent('Merhaba, profesyonel temizlik hizmeti için randevu almak istiyorum.')}`;

export default function AppointmentForm() {
  const ref = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [note, setNote] = useState('');
  const NOTE_MAX = 500;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add('opacity-100', 'translate-y-0'); },
      { threshold: 0.05 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (note.length > NOTE_MAX) return;
    setStatus('loading');
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((val, key) => data.append(key, val.toString()));
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setNote('');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="appointment" className="w-full bg-[#f8faf9] py-16 md:py-24">
      <div className="max-w-6xl mx-auto px-4">
        <div
          ref={ref}
          className="opacity-0 translate-y-8 transition-all duration-700"
        >
          <div className="grid grid-cols-1 lg:grid-cols-5 rounded-3xl overflow-hidden border border-gray-100">
            {/* Left - Info */}
            <div className="lg:col-span-2 bg-[#1a1a1a] p-8 md:p-10 flex flex-col justify-between">
              <div>
                <span className="inline-block text-emerald-400 font-semibold text-xs uppercase tracking-widest mb-4">
                  Hızlı Randevu
                </span>
                <h2
                  className="text-2xl md:text-3xl font-extrabold text-white leading-tight mb-5"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  Hemen Randevu Alın
                </h2>
                <p className="text-gray-400 text-sm leading-relaxed mb-8">
                  Formu doldurun, ekibimiz en kısa sürede sizi arasın. Ya da WhatsApp&apos;tan anında randevu oluşturun.
                </p>

                <div className="flex flex-col gap-3 mb-8">
                  {advantages.map((a) => (
                    <div key={a} className="flex items-start gap-2">
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-emerald-400" />
                      </span>
                      <span className="text-gray-300 text-sm">{a}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <a href={`tel:${PHONE_1}`} className="flex items-center gap-2 text-white hover:text-emerald-400 transition-colors font-semibold whitespace-nowrap">
                  <span className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-full">
                    <i className="ri-phone-fill text-emerald-400 text-sm" />
                  </span>
                  {PHONE_1_DISPLAY}
                </a>
                <a href={`tel:${PHONE_2}`} className="flex items-center gap-2 text-white hover:text-emerald-400 transition-colors font-semibold whitespace-nowrap">
                  <span className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-full">
                    <i className="ri-phone-fill text-emerald-400 text-sm" />
                  </span>
                  {PHONE_2_DISPLAY}
                </a>
                <a
                  href={WA_LINK}
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold text-sm px-5 py-3 rounded-xl mt-2 transition-all hover:scale-105 whitespace-nowrap"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-whatsapp-line text-base" />
                  </span>
                  WhatsApp ile Randevu Al
                </a>
              </div>
            </div>

            {/* Right - Form */}
            <div className="lg:col-span-3 bg-white p-8 md:p-10">
              {status === 'success' ? (
                <div className="h-full flex flex-col items-center justify-center text-center gap-4 py-10">
                  <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full">
                    <i className="ri-check-double-line text-emerald-600 text-3xl" />
                  </div>
                  <h3 className="text-xl font-bold text-[#1a1a1a]">Randevunuz Alındı!</h3>
                  <p className="text-gray-500 text-sm max-w-xs">
                    En kısa sürede sizi arayacağız. İyi günler dileriz!
                  </p>
                  <button
                    onClick={() => setStatus('idle')}
                    className="text-emerald-600 font-semibold text-sm hover:underline cursor-pointer"
                  >
                    Yeni Randevu Al
                  </button>
                </div>
              ) : (
                <form data-readdy-form id="randevu-formu" onSubmit={handleSubmit} className="flex flex-col gap-5">
                  <h3 className="font-bold text-[#1a1a1a] text-xl mb-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Randevu Formu
                  </h3>

                  {/* Row 1 */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Ad Soyad *</label>
                      <input
                        type="text"
                        name="adSoyad"
                        required
                        placeholder="Adınız Soyadınız"
                        className="w-full text-sm border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-emerald-400 transition-colors placeholder-gray-300"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Telefon *</label>
                      <input
                        type="tel"
                        name="telefon"
                        required
                        placeholder="0 5__ ___ __ __"
                        className="w-full text-sm border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-emerald-400 transition-colors placeholder-gray-300"
                      />
                    </div>
                  </div>

                  {/* Row 2 */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Hizmet Türü *</label>
                      <select
                        name="hizmetTuru"
                        required
                        className="w-full text-sm border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-emerald-400 transition-colors text-gray-700 cursor-pointer"
                      >
                        <option value="">Seçiniz</option>
                        {serviceOptions.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">İlçe *</label>
                      <input
                        type="text"
                        name="ilce"
                        required
                        placeholder="İlçenizi yazın"
                        className="w-full text-sm border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-emerald-400 transition-colors placeholder-gray-300"
                      />
                    </div>
                  </div>

                  {/* Row 3 */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Tarih *</label>
                      <input
                        type="date"
                        name="tarih"
                        required
                        className="w-full text-sm border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-emerald-400 transition-colors text-gray-700 cursor-pointer"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Saat *</label>
                      <select
                        name="saat"
                        required
                        className="w-full text-sm border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-emerald-400 transition-colors text-gray-700 cursor-pointer"
                      >
                        <option value="">Saat seçin</option>
                        {['08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00','18:00'].map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Note */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      Not <span className="font-normal text-gray-400">({note.length}/{NOTE_MAX})</span>
                    </label>
                    <textarea
                      name="not"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      maxLength={NOTE_MAX}
                      rows={3}
                      placeholder="Eklemek istediğiniz bilgileri yazın..."
                      className="w-full text-sm border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-emerald-400 transition-colors resize-none placeholder-gray-300"
                    />
                    {note.length >= NOTE_MAX && (
                      <p className="text-red-500 text-xs mt-1">Maksimum karakter sınırına ulaştınız.</p>
                    )}
                  </div>

                  {status === 'error' && (
                    <p className="text-red-500 text-sm bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                      Bir hata oluştu. Lütfen tekrar deneyin veya bizi arayın.
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'loading' || note.length > NOTE_MAX}
                    className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-300 text-white font-bold text-base py-4 rounded-xl transition-all hover:scale-[1.02] whitespace-nowrap"
                  >
                    {status === 'loading' ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin" /> Gönderiliyor...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-calendar-check-line" /> Randevu Oluştur
                      </span>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
